import {useCallback, useEffect, useMemo, useState} from "react";
import {Modal} from "../../../components";
import {formatApiError, listTeams} from "../../../api/admin";
import {getBookRows, settleBookRow} from "../../../api/admin/bookRows";
import type {BookRowItem, BookRowsResponse} from "../../../api/admin/bookRows";
import {formatDate} from "../../../utils";

export function BookRowSettlementSection({merchantId, token, onSettled}: {
    merchantId: string;
    token: string | null;
    onSettled?: (message: string) => void;
}) {
    const [data, setData] = useState<BookRowsResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [selected, setSelected] = useState<BookRowItem | null>(null);
    const [teams, setTeams] = useState<{id: string; name: string}[]>([]);
    const [teamId, setTeamId] = useState("");
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try { setData(await getBookRows(token, merchantId)); }
        catch (cause) { setError(formatApiError(cause)); }
        finally { setLoading(false); }
    }, [token, merchantId]);
    useEffect(() => { void load(); }, [load]);
    const filtered = useMemo(() => (data?.items ?? []).slice().reverse().filter(row =>
        !search.trim() || row.externalId.includes(search.trim()) || row.externalOrderId?.includes(search.trim())
    ), [data, search]);
    async function open(row: BookRowItem) {
        setSelected(row); setTeamId(""); setTeams([]); setFormError(null); setSuccess(null);
        if (!token) return;
        try {
            const response = await listTeams(token, {limit: 200});
            setTeams(response.items.map(team => ({id: team.id, name: team.name})));
        } catch (cause) { setFormError(formatApiError(cause)); }
    }
    async function confirm() {
        if (!token || !selected || !teamId || busy) return;
        setBusy(true); setFormError(null);
        try {
            await settleBookRow(token, merchantId, selected.externalId, teamId);
            const message = `Строка ${selected.externalId} разнесена.`;
            setSelected(null); setSuccess(message); onSettled?.(message);
            await load();
        } catch (cause) { setFormError(formatApiError(cause)); }
        finally { setBusy(false); }
    }
    const teamName = teams.find(team => team.id === teamId)?.name ?? "выберите команду";
    return <section className="merchant-rate-block">
        <div className="section-header">
            <div><h3>Строки книги кабинета</h3><p className="section-hint">Ручная разноска доступна для строк с заявкой, по которым ещё нет проводок.</p></div>
            <button type="button" className="btn btn-ghost btn-sm" disabled={loading} onClick={() => void load()}>Обновить строки</button>
        </div>
        {error && <div className="form-error" role="alert">{error}</div>}
        {success && <p role="status">{success}</p>}
        <label className="form-field"><span>Номер строки или заявки</span><input value={search} onChange={event => {setSearch(event.target.value); setPage(0);}} /></label>
        <table className="platform-closed-table">
            <thead><tr><th>Строка / заявка</th><th>Время и тип</th><th>Δ Main</th><th>Δ Cashback</th><th>Разноска</th></tr></thead>
            <tbody>{filtered.slice(page * 50, (page + 1) * 50).map(row => <tr key={row.externalId}>
                <td className="mono">{row.externalId} / {row.externalOrderId ?? "без заявки"}</td>
                <td>{formatDate(row.occurredAt)}<br />{row.operationType}</td>
                <td className="mono">{row.mainDelta} {data?.currency}</td><td className="mono">{row.cashbackDelta} {data?.currency}</td>
                <td>{row.manualActor ? <>Разнесено руками, {row.manualActor}, {row.postedAt ? formatDate(row.postedAt) : "—"}</> :
                    row.canSettle ? <button type="button" className="btn btn-primary btn-sm" onClick={() => void open(row)}>Разнести руками</button> : row.refusal}</td>
            </tr>)}</tbody>
        </table>
        {!loading && !filtered.length && <p>Строки не найдены.</p>}
        {filtered.length > 50 && <div className="modal-actions">
            <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>Назад</button>
            <span>Страница {page + 1}</span>
            <button type="button" disabled={(page + 1) * 50 >= filtered.length} onClick={() => setPage(page + 1)}>Далее</button>
        </div>}
        <Modal open={selected !== null} title="Разнести строку книги" onClose={() => {if (!busy) setSelected(null);}}>
            {selected && <>
                <p>Строка <strong>{selected.externalId}</strong>, заявка <strong>{selected.externalOrderId}</strong>: {selected.operationType}.</p>
                <p>Время операции: {formatDate(selected.occurredAt)}. Δ Main: {selected.mainDelta}; Δ Cashback: {selected.cashbackDelta} {data?.currency}.</p>
                <label className="form-field"><span>Команда</span><select value={teamId} disabled={busy} onChange={event => setTeamId(event.target.value)}>
                    <option value="">— выберите команду —</option>
                    {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
                </select></label>
                <p>Будут записаны следующие проводки:</p>
                <ul>{selected.postings.map(posting => <li key={posting.key}>
                    {posting.side === "debt-correction" ? `Мерчант ${data?.merchantName}` : `Команда ${teamName}`}: <strong>{posting.amount} {data?.currency}</strong>, {posting.reason}; <span className="mono">{posting.key}</span>
                </li>)}</ul>
                {!selected.postings.some(posting => posting.side === "debt-correction") && <p>Сторона мерчанта для этой строки не записывается.</p>}
                {formError && <div className="form-error" role="alert">{formError}</div>}
                <div className="modal-actions">
                    <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setSelected(null)}>Отмена</button>
                    <button type="button" className="btn btn-primary" disabled={busy || !teamId} onClick={() => void confirm()}>{busy ? "Проводим…" : "Подтвердить разноску"}</button>
                </div>
            </>}
        </Modal>
    </section>;
}
